import React, { useEffect, useRef, useState } from 'react';
import { LookerEmbedSDK } from "@looker/embed-sdk";
import { API } from "aws-amplify";
import { Box, CircularProgress } from "@mui/material";
import { ensureLookerSDKInitialized } from "./LookerSdkInitializer";
import { isAiInlineSummarizationEnabled } from "@/shared/access";
import AiSummaryBox from "@/common/AiSummaryBox";
import useDocumentTitle from "../../common/UseDocumentTitle";

// To add a new AI-enabled dashboard: add one entry here — no other code changes needed.
const DASHBOARD_AI_CONFIG = {
    'survey__department': { containerId: 'looker-dashboard-ai-summary-box' },
    'survey__calltaker':  { containerId: 'looker-calltaker-ai-summary-box' },
    'survey__responder':  { containerId: 'looker-responder-ai-summary-box' },
    'survey__inbound':    { containerId: 'looker-inbound-ai-summary-box' },
    'survey__responses':  { containerId: 'looker-responses-ai-summary-box' },
    'rules':              { containerId: 'looker-rules-ai-summary-box' },
};

const DEFAULT_AI_CONTAINER_ID = 'looker-dashboard-ai-summary-box';

const isAiSupportedDashboard = (dashboardId) =>
    dashboardId != null && Object.keys(DASHBOARD_AI_CONFIG).some(key => dashboardId.includes(key));

const getAiContainerId = (dashboardId) => {
    const match = Object.keys(DASHBOARD_AI_CONFIG).find(key => dashboardId?.includes(key));
    return match ? DASHBOARD_AI_CONFIG[match].containerId : DEFAULT_AI_CONTAINER_ID;
};

const LookerDashboard = ({ activeDashboard, userInfo }) => {
    useDocumentTitle(activeDashboard?.name);

    const embedContainerRef = useRef(null);

    const activeFiltersRef = useRef({});
    const [aiSummaryVisible, setAiSummaryVisible] = useState(false);
    const [summaryKey, setSummaryKey] = useState(0);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const isInitialRunRef = useRef(true);

    const theme = userInfo && userInfo.lightTheme ? 'light' : 'dark';
    const dashboardId = activeDashboard?.dashboardId;

    // Re-evaluate AI summary visibility whenever the dashboard changes
    useEffect(() => {
        let cancelled = false;
        if (!isAiInlineSummarizationEnabled() || !isAiSupportedDashboard(dashboardId)) {
            setAiSummaryVisible(false);
            return;
        }
        API.get('Core', '/api/v1/ai-administration')
            .then(response => {
                if (cancelled) return;
                const summaryEnabled = response.find(s => s.flag === 'AI_SUMMARY_SETTING');
                const excludeReports = response.find(s => s.flag === 'AI_SUMMARY_EXCLUDE_REPORTS');
                const isSummaryOn = summaryEnabled ? !!summaryEnabled.enabled : false;
                const isExcluded = excludeReports ? !!excludeReports.enabled : false;
                setAiSummaryVisible(isSummaryOn && !isExcluded);
            })
            .catch(() => { if (!cancelled) setAiSummaryVisible(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardId]);

    // Embed the dashboard and attach filter listeners
    useEffect(() => {
        let cancelled = false;
        let loadingTimeout = null;

        activeFiltersRef.current = {};
        setSummaryKey(k => k + 1);
        setDashboardLoading(true);
        isInitialRunRef.current = true;

        // Clears the spinner once — safe to call from any event path.
        const clearSpinner = () => {
            clearTimeout(loadingTimeout);
            if (!cancelled) setDashboardLoading(false);
        };

        const LOADING_TIMEOUT_MS = 120000;
        // 2-minute fallback: if run:complete and run:error both never fire, dismiss the spinner.
        loadingTimeout = setTimeout(clearSpinner, LOADING_TIMEOUT_MS);

        const embedDashboard = async () => {
            try {
                ensureLookerSDKInitialized();
                // Always read embedContainerRef.current here — not the stale `container`
                // captured above, which may point to a detached node after a re-render.
                const currentContainer = embedContainerRef.current;
                if (!currentContainer) return;
                currentContainer.innerHTML = '';

                const handleFiltersChanged = (event) => {
                    activeFiltersRef.current = event?.dashboard?.dashboard_filters || {};
                };

                const builder = LookerEmbedSDK.createDashboardWithId(dashboardId);
                if (theme === 'dark') {
                    builder.withTheme('PowerEngage_Dark');
                }

                // Events must be registered on the builder chain before .build().connect()
                const connectedDashboard = await builder
                    .appendTo(currentContainer)
                    .withDialogScroll()
                    .withAllowAttr('fullscreen')
                    .withClassName('lookerEmbed')
                    .on('dashboard:filters:changed', handleFiltersChanged)
                    .on('dashboard:run:start', (event) => {
                        handleFiltersChanged(event);
                        if (!isInitialRunRef.current) {
                            setSummaryKey(k => k + 1);
                        }
                    })
                    .on('dashboard:run:complete', () => {
                        isInitialRunRef.current = false;
                        clearSpinner();
                    })
                    .on('dashboard:run:error', () => {
                        isInitialRunRef.current = false;
                        clearSpinner();
                    })
                    .build()
                    .connect()
                    .catch((error) => {
                        console.error('Looker Dashboard failed to load', error);
                        clearSpinner();
                        return null;
                    });

                if (cancelled || !connectedDashboard) return;

            } catch (error) {
                console.error('Dashboard embedding failed:', error);
                clearSpinner();
            }
        };

        embedDashboard();

        return () => {
            cancelled = true;
            clearTimeout(loadingTimeout);
            if (embedContainerRef.current) {
                embedContainerRef.current.innerHTML = '';
            }
        };
    }, [dashboardId]);

    const fetchAiSummary = async () => {
        try {
            const response = await API.post('Core', '/api/v1/looker/summary', {
                queryStringParameters: {
                    dashboardId,
                },
                body: activeFiltersRef.current,
            });
            return {
                success: response.success !== false,
                response: response.summary,
                responseType: 'html',
            };
        } catch (err) {
            return {
                success: false,
                response: '<p>Failed to generate summary. Please try again.</p>',
                responseType: 'html',
            };
        }
    };

    const aiContainerId = getAiContainerId(dashboardId);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {dashboardLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress aria-label="Loading dashboard" />
                </Box>
            )}
            {aiSummaryVisible && !dashboardLoading && (
                <div style={{ padding: '0 24px 2px 24px', flexShrink: 0 }}>
                    <div style={{ maxHeight: '250px', overflowY: 'auto' }} tabIndex="0">
                        <AiSummaryBox
                            key={summaryKey}
                            containerId={aiContainerId}
                            onSummaryRequested={fetchAiSummary}
                        />
                    </div>
                </div>
            )}
            <div id="looker-dashboard-container" ref={embedContainerRef} style={{ flex: 1, minHeight: 0, display: dashboardLoading ? 'none' : undefined }} />
        </div>
    );
};

export default LookerDashboard;
